import {
  fieldTypeRegistry,
  getFieldTypeDefinition,
  type FieldOwnership,
  type FormFieldType,
} from '@dispatch/domain';
import type {
  TemplateCalculation,
  TemplateConditionExpression,
  TemplateDefinition,
  TemplateField,
  TemplateRule,
} from '@dispatch/schemas';

export type IdFactory = () => string;

const phase4DeferredCaptureFieldTypes = new Set<FormFieldType>([
  'file_upload',
  'single_photograph',
  'multiple_photographs',
  'signature',
  'gps',
  'map_location',
]);

export const phase4FieldTypes = fieldTypeRegistry.filter(
  (fieldType) =>
    fieldType.phaseIntroduced <= 4 || phase4DeferredCaptureFieldTypes.has(fieldType.id),
);

export function cloneDefinition(definition: TemplateDefinition): TemplateDefinition {
  return structuredClone(definition);
}

export function nextSymbolicId(prefix: string, existingIds: readonly string[]): string {
  const used = new Set(existingIds);
  let index = 1;
  while (used.has(`${prefix}_${index}`)) index += 1;
  return `${prefix}_${index}`;
}

export function defaultOwnership(type: FormFieldType): FieldOwnership {
  return getFieldTypeDefinition(type).allowedOwnerships[0] ?? 'engineer';
}

function choiceOptions(type: FormFieldType): TemplateField['options'] {
  if (!['dropdown', 'radio_buttons', 'checkboxes'].includes(type)) return undefined;
  return [{ id: 'option_1', label: 'Option 1', value: 'option_1' }];
}

function basicField(type: FormFieldType, id: string, label?: string): TemplateField {
  const field: TemplateField = {
    id,
    type,
    label: label ?? getFieldTypeDefinition(type).label,
    ownership: defaultOwnership(type),
    required: false,
    ...(choiceOptions(type) ? { options: choiceOptions(type) } : {}),
  };

  if (type === 'file_upload') {
    field.fileUpload = { multiple: false, maxFiles: 1 };
  }
  if (type === 'multiple_photographs') {
    field.photographs = { minPhotos: 0, maxPhotos: 10 };
  }
  if (type === 'signature') {
    field.signature = { signerType: 'site_contact' };
  }
  if (type === 'system_field') {
    field.systemSource = 'job_number';
  }

  return field;
}

export function addPage(definition: TemplateDefinition): {
  definition: TemplateDefinition;
  pageId: string;
  sectionId: string;
} {
  const next = cloneDefinition(definition);
  const pageId = nextSymbolicId(
    'page',
    next.pages.map((page) => page.id),
  );
  const sectionId = nextSymbolicId(
    'section',
    next.sections.map((section) => section.id),
  );
  next.pages.push({
    id: pageId,
    title: `Page ${next.pages.length + 1}`,
    sectionIds: [sectionId],
  });
  next.sections.push({
    id: sectionId,
    title: 'Section',
    pageId,
    fieldIds: [],
  });
  return { definition: next, pageId, sectionId };
}

export function addSection(
  definition: TemplateDefinition,
  pageId: string,
): {
  definition: TemplateDefinition;
  sectionId: string;
} {
  const next = cloneDefinition(definition);
  const page = next.pages.find((candidate) => candidate.id === pageId);
  if (!page) return { definition: next, sectionId: '' };
  const sectionId = nextSymbolicId(
    'section',
    next.sections.map((section) => section.id),
  );
  page.sectionIds.push(sectionId);
  next.sections.push({
    id: sectionId,
    title: `Section ${page.sectionIds.length}`,
    pageId,
    fieldIds: [],
  });
  return { definition: next, sectionId };
}

function createNestedChild(
  definition: TemplateDefinition,
  idFactory: IdFactory,
  label: string,
): TemplateField {
  let id = idFactory();
  const existingIds = new Set(definition.fields.map((field) => field.id));
  while (existingIds.has(id)) id = idFactory();
  return basicField('single_line_text', id, label);
}

export function addField(
  definition: TemplateDefinition,
  sectionId: string,
  type: FormFieldType,
  idFactory: IdFactory = () => crypto.randomUUID(),
  insertionIndex?: number,
): { definition: TemplateDefinition; fieldId: string } {
  const next = cloneDefinition(definition);
  const section = next.sections.find((candidate) => candidate.id === sectionId);
  if (!section) return { definition: next, fieldId: '' };

  let fieldId = idFactory();
  const existingIds = new Set(next.fields.map((field) => field.id));
  while (existingIds.has(fieldId)) fieldId = idFactory();

  let field = basicField(type, fieldId);
  const additionalFields: TemplateField[] = [];

  if (type === 'repeating_section') {
    const child = createNestedChild(next, idFactory, 'Item');
    additionalFields.push(child);
    field = {
      ...field,
      repeating: {
        fieldIds: [child.id],
        minEntries: 0,
        maxEntries: 10,
        addAnotherLabel: 'Add another',
      },
    };
  }

  if (type === 'table_grid') {
    const child = createNestedChild(next, idFactory, 'Column 1');
    additionalFields.push(child);
    field = {
      ...field,
      grid: {
        columns: [{ id: 'column_1', label: 'Column 1', fieldIds: [child.id] }],
      },
    };
  }

  next.fields.push(field, ...additionalFields);
  const targetIndex =
    insertionIndex === undefined
      ? section.fieldIds.length
      : Math.max(0, Math.min(insertionIndex, section.fieldIds.length));
  section.fieldIds.splice(targetIndex, 0, field.id);
  return { definition: next, fieldId: field.id };
}

export function addNestedField(
  definition: TemplateDefinition,
  parentFieldId: string,
  type: FormFieldType,
  idFactory: IdFactory = () => crypto.randomUUID(),
): { definition: TemplateDefinition; fieldId: string } {
  const next = cloneDefinition(definition);
  const parent = next.fields.find((field) => field.id === parentFieldId);
  if (!parent || ['repeating_section', 'table_grid'].includes(type)) {
    return { definition: next, fieldId: '' };
  }
  let fieldId = idFactory();
  const existingIds = new Set(next.fields.map((field) => field.id));
  while (existingIds.has(fieldId)) fieldId = idFactory();
  const child = basicField(type, fieldId);
  next.fields.push(child);

  if (parent.type === 'repeating_section' && parent.repeating) {
    parent.repeating.fieldIds.push(child.id);
    return { definition: next, fieldId: child.id };
  }
  if (parent.type === 'table_grid' && parent.grid) {
    const columnId = nextSymbolicId(
      'column',
      parent.grid.columns.map((column) => column.id),
    );
    parent.grid.columns.push({
      id: columnId,
      label: `Column ${parent.grid.columns.length + 1}`,
      fieldIds: [child.id],
    });
    return { definition: next, fieldId: child.id };
  }

  next.fields = next.fields.filter((field) => field.id !== child.id);
  return { definition: next, fieldId: '' };
}

export function reorderIds(values: readonly string[], activeId: string, overId: string): string[] {
  const from = values.indexOf(activeId);
  const to = values.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return [...values];
  const next = [...values];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...values];
  next.splice(to, 0, moved);
  return next;
}

export function moveId(values: readonly string[], id: string, direction: -1 | 1): string[] {
  const index = values.indexOf(id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= values.length) return [...values];
  const next = [...values];
  const [moved] = next.splice(index, 1);
  if (moved === undefined) return [...values];
  next.splice(target, 0, moved);
  return next;
}

export function removeField(definition: TemplateDefinition, fieldId: string): TemplateDefinition {
  const next = cloneDefinition(definition);
  const field = next.fields.find((candidate) => candidate.id === fieldId);
  if (!field) return next;

  const removeIds = new Set<string>([fieldId]);
  if (field.type === 'repeating_section' && field.repeating) {
    for (const childId of field.repeating.fieldIds) removeIds.add(childId);
  }
  if (field.type === 'table_grid' && field.grid) {
    for (const column of field.grid.columns) {
      for (const childId of column.fieldIds) removeIds.add(childId);
    }
  }

  next.fields = next.fields.filter((candidate) => !removeIds.has(candidate.id));
  for (const section of next.sections) {
    section.fieldIds = section.fieldIds.filter((id) => !removeIds.has(id));
  }
  for (const candidate of next.fields) {
    if (candidate.type === 'repeating_section' && candidate.repeating) {
      candidate.repeating.fieldIds = candidate.repeating.fieldIds.filter(
        (id) => !removeIds.has(id),
      );
    }
    if (candidate.type === 'table_grid' && candidate.grid) {
      candidate.grid.columns = candidate.grid.columns
        .map((column) => ({
          ...column,
          fieldIds: column.fieldIds.filter((id) => !removeIds.has(id)),
        }))
        .filter((column) => column.fieldIds.length > 0);
    }
  }
  next.rules = next.rules.filter((rule) => {
    const targetRemoved = rule.targetFieldId ? removeIds.has(rule.targetFieldId) : false;
    return !targetRemoved && !conditionReferencesAny(rule, removeIds);
  });
  next.calculations = next.calculations.filter(
    (calculation) =>
      !removeIds.has(calculation.targetFieldId) &&
      !calculation.sourceFieldIds.some((sourceId) => removeIds.has(sourceId)),
  );
  next.referenceBindings = next.referenceBindings.filter(
    (binding) => !removeIds.has(binding.fieldId),
  );
  return next;
}

function expressionReferencesAny(
  expression: TemplateConditionExpression,
  fieldIds: ReadonlySet<string>,
): boolean {
  if (expression.kind === 'condition') return fieldIds.has(expression.fieldId);
  return expression.conditions.some((condition) => expressionReferencesAny(condition, fieldIds));
}

function conditionReferencesAny(rule: TemplateRule, fieldIds: ReadonlySet<string>): boolean {
  if ('when' in rule) return expressionReferencesAny(rule.when, fieldIds);
  return rule.conditions.some((condition) => fieldIds.has(condition.fieldId));
}

export function calculationForField(
  definition: TemplateDefinition,
  fieldId: string,
): TemplateCalculation | undefined {
  return definition.calculations.find((calculation) => calculation.targetFieldId === fieldId);
}

export function upsertCalculation(
  definition: TemplateDefinition,
  calculation: TemplateCalculation,
): TemplateDefinition {
  const next = cloneDefinition(definition);
  next.calculations = next.calculations.filter(
    (candidate) => candidate.targetFieldId !== calculation.targetFieldId,
  );
  next.calculations.push(calculation);
  return next;
}

export function removeCalculation(
  definition: TemplateDefinition,
  targetFieldId: string,
): TemplateDefinition {
  const next = cloneDefinition(definition);
  next.calculations = next.calculations.filter(
    (calculation) => calculation.targetFieldId !== targetFieldId,
  );
  return next;
}

export function rootConditionGroup(
  fields: readonly TemplateField[],
): TemplateConditionExpression | undefined {
  const source = fields.find((field) => field.type !== 'heading' && field.type !== 'divider');
  if (!source) return undefined;
  return {
    kind: 'group',
    mode: 'all',
    conditions: [{ kind: 'condition', fieldId: source.id, operator: 'is_not_empty' }],
  };
}

export function createRule(
  definition: TemplateDefinition,
  effect: TemplateRule['effect'],
): TemplateRule | undefined {
  const when = rootConditionGroup(definition.fields);
  if (!when) return undefined;
  const id = nextSymbolicId(
    'rule',
    definition.rules.map((rule) => rule.id),
  );
  const firstField = definition.fields[0];
  const firstSection = definition.sections[0];

  if (effect === 'display_warning' || effect === 'prevent_submission') {
    return {
      id,
      effect,
      when,
      message: effect === 'display_warning' ? 'Warning' : 'Cannot submit',
    };
  }
  if (effect === 'navigate_to_section') {
    if (!firstSection) return undefined;
    return { id, effect, when, targetSectionId: firstSection.id };
  }
  if (!firstField) return undefined;
  return {
    id,
    effect,
    when,
    targetFieldId: firstField.id,
    ...(effect === 'set_value' ? { value: '' } : {}),
  };
}

export function moveFieldToSection(
  definition: TemplateDefinition,
  fieldId: string,
  targetSectionId: string,
): TemplateDefinition {
  const next = cloneDefinition(definition);
  const target = next.sections.find((section) => section.id === targetSectionId);
  if (!target) return next;
  for (const section of next.sections) {
    section.fieldIds = section.fieldIds.filter((candidate) => candidate !== fieldId);
  }
  target.fieldIds.push(fieldId);
  return next;
}

export function removeSection(
  definition: TemplateDefinition,
  sectionId: string,
): TemplateDefinition {
  const section = definition.sections.find((candidate) => candidate.id === sectionId);
  if (!section) return cloneDefinition(definition);
  const page = definition.pages.find((candidate) => candidate.id === section.pageId);
  if (!page || page.sectionIds.length <= 1) return cloneDefinition(definition);

  let next = cloneDefinition(definition);
  for (const fieldId of section.fieldIds) next = removeField(next, fieldId);
  next.sections = next.sections.filter((candidate) => candidate.id !== sectionId);
  const targetPage = next.pages.find((candidate) => candidate.id === section.pageId);
  if (targetPage) targetPage.sectionIds = targetPage.sectionIds.filter((id) => id !== sectionId);
  next.rules = next.rules.filter((rule) => rule.targetSectionId !== sectionId);
  return next;
}

export function removePage(definition: TemplateDefinition, pageId: string): TemplateDefinition {
  if (definition.pages.length <= 1) return cloneDefinition(definition);
  const page = definition.pages.find((candidate) => candidate.id === pageId);
  if (!page) return cloneDefinition(definition);

  let next = cloneDefinition(definition);
  for (const sectionId of page.sectionIds) {
    const section = next.sections.find((candidate) => candidate.id === sectionId);
    if (!section) continue;
    for (const fieldId of section.fieldIds) next = removeField(next, fieldId);
    next.sections = next.sections.filter((candidate) => candidate.id !== sectionId);
    next.rules = next.rules.filter((rule) => rule.targetSectionId !== sectionId);
  }
  next.pages = next.pages.filter((candidate) => candidate.id !== pageId);
  return next;
}
